import React from 'react';
import PropTypes from 'prop-types';
import TableBody from './TableBody';
import TableHead from './TableHead';
import LoadingState, { LoadingData } from './LoadingState';
import Spinner from '../Loaders/Spinner';
import EmptyState from './EmptyState';
import TablePaginator from './TablePaginator';
import Table from './Table';

import './Table.scss';

interface CommonTableProps {
  tableHeader: Object;
  tableData: Object[];
  isLoading: boolean;
  loadingData: LoadingData;
  emptyRowSize: number;
  loader: React.ReactElement;
  id: string;
  onRowClick: Function;
  pageLength: number;
  pageNumber: number;
  totalRows: number;
  handlePageChange: (event: React.ChangeEvent<unknown>, page: number) => void;
  emptyStateComponent: React.ReactElement | null;
  emptyStateMessage: string;
}


function CommonTable({
  tableHeader,
  tableData,
  isLoading,
  loadingData,
  emptyRowSize,
  loader,
  id,
  onRowClick,
  pageLength,
  pageNumber,
  totalRows,
  handlePageChange,
  emptyStateComponent,
  emptyStateMessage,
}: CommonTableProps) {
  if (!isLoading && !tableData.length) {
    if (emptyStateComponent) return React.cloneElement(emptyStateComponent);
    return (
      <EmptyState
        rowSize={emptyRowSize}
        tableHeader={tableHeader}
        id={id}
        emptyStateMessage={emptyStateMessage}
      />
    );
  }

  if (isLoading && !tableData.length) {
    return (
      <LoadingState
        loadingData={loadingData}
        loader={loader}
        id={id}
      />
    );
  }

  return (
    <div className="common__table_container">
      <Table
        tableHead={<TableHead tableHeader={tableHeader} />}
        tableBody={(
          <TableBody
            tableHeader={tableHeader}
            tableData={tableData}
            onRowClick={onRowClick}
          />
        )}
        loader={loader}
        id={id}
        isLoading={isLoading}
      />
      <TablePaginator
        totalRows={totalRows}
        pageLength={pageLength}
        pageNumber={pageNumber}
        handlePageChange={handlePageChange}
      />
    </div>
  );
}

CommonTable.defaultProps = {
  isLoading: true,
  loadingData: {
    rowSize: 5,
    colSize: 4,
  },
  emptyRowSize: 5,
  loader: <Spinner />,
  id: '',
  onRowClick: () => {},
  emptyStateComponent: null,
  emptyStateMessage: 'No recorded usage found',
};

CommonTable.propTypes = {
  tableHeader: PropTypes.object.isRequired,
  tableData: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  loadingData: PropTypes.object,
  emptyRowSize: PropTypes.number,
  loader: PropTypes.element,
  id: PropTypes.string,
  onRowClick: PropTypes.func,
  pageNumber: PropTypes.number.isRequired,
  pageLength: PropTypes.number.isRequired,
  totalRows: PropTypes.number.isRequired,
  handlePageChange: PropTypes.func.isRequired,
  emptyStateComponent: PropTypes.element,
  emptyStateMessage: PropTypes.string
};

export default CommonTable;
